import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly translateService = inject(TranslateService);

  public showSuccessNotificationWithLink(
    containerId: string,
    translationKey: string,
    link: string,
    onUploadAgain?: () => void
  ): void {
    this.translateService.get(translationKey).subscribe((message: string) => {
      this.createNotificationWithLink(containerId, message, 'success', link, onUploadAgain);
    });
  }
  public showErrorNotification(containerId: string, translationKeyObj: string | string[], errorCode?: string): void {
    if (Array.isArray(translationKeyObj)) {
      translationKeyObj.forEach((key) => {
        this.translateService.get(key).subscribe((message) => {
          this.createNotification(containerId, message, 'warning');
        });
      });
    } else {
      this.translateService.get(translationKeyObj).subscribe((message) => {
        this.createNotification(containerId, message + (errorCode ? '(' + errorCode + ')' : ''), 'warning');
      });
    }
  }

  public showErrorNotificationWithLink(containerId: string, onTryAgain: () => void): void {
    this.createErrorNotificationWithLink(containerId, onTryAgain);
  }
  private createNotificationWithLink(
    containerId: string,
    message: string,
    notificationType: string,
    link: string,
    onUploadAgain?: () => void
  ): void {
    const container = document.getElementById(containerId);

    if (container) {
      container.style.margin = '25px';
      container.style.marginRight = '40px';
      const card = document.createElement('sdx-card');
      card.setAttribute('layout', 'inline-notification');
      card.setAttribute('notification-type', notificationType);
      card.setAttribute('label', message);
      card.setAttribute('label-aria-level', '4');

      this.translateService
        .get([
          'App.Notifications.UploadSuccessDescription',
          'App.Notifications.PdfLink',
          'App.Notifications.UploadAgain',
        ])
        .subscribe((translations: Record<string, string>) => {
          const linksContainer = document.createElement('div');
          linksContainer.style.display = 'flex';
          linksContainer.style.flexDirection = 'column';
          linksContainer.style.alignItems = 'flex-start';
          linksContainer.style.gap = '8px';
          linksContainer.style.marginTop = '8px';

          const description = document.createElement('p');
          description.style.margin = '0';
          description.textContent = translations['App.Notifications.UploadSuccessDescription'];
          linksContainer.appendChild(description);

          const openAnchor = document.createElement('a');
          openAnchor.className = 'link link--standalone';
          openAnchor.style.marginLeft = '0';
          openAnchor.href = link;
          openAnchor.target = '_blank';
          openAnchor.textContent = translations['App.Notifications.PdfLink'];
          linksContainer.appendChild(openAnchor);

          if (onUploadAgain) {
            const uploadAgainAnchor = document.createElement('a');
            uploadAgainAnchor.className = 'link link--standalone';
            uploadAgainAnchor.style.marginLeft = '0';
            uploadAgainAnchor.href = '#';
            uploadAgainAnchor.textContent = translations['App.Notifications.UploadAgain'];
            uploadAgainAnchor.addEventListener('click', (e) => {
              e.preventDefault();
              onUploadAgain();
            });
            linksContainer.appendChild(uploadAgainAnchor);
          }

          card.appendChild(linksContainer);
        });

      container.appendChild(this.wrapCard(card));
    } else {
      console.error(`Container with ID '${containerId}' not found.`);
    }
  }

  private createErrorNotificationWithLink(containerId: string, onTryAgain: () => void): void {
    const container = document.getElementById(containerId);

    if (container) {
      const card = document.createElement('sdx-card');
      card.setAttribute('layout', 'inline-notification');
      card.setAttribute('notification-type', 'error');
      card.setAttribute('label-aria-level', '4');
      card.style.marginLeft = '25px';

      this.translateService
        .get(['App.Notifications.ErrorTitle', 'App.Notifications.ErrorDescription', 'App.Notifications.TryAgain'])
        .subscribe((translations: Record<string, string>) => {
          card.setAttribute('label', translations['App.Notifications.ErrorTitle']);

          const contentContainer = document.createElement('div');
          contentContainer.style.display = 'flex';
          contentContainer.style.flexDirection = 'column';
          contentContainer.style.alignItems = 'flex-start';
          contentContainer.style.gap = '8px';
          contentContainer.style.marginTop = '8px';

          const description = document.createElement('p');
          description.style.margin = '0';
          description.textContent = translations['App.Notifications.ErrorDescription'];
          contentContainer.appendChild(description);

          const tryAgainAnchor = document.createElement('a');
          tryAgainAnchor.className = 'link link--standalone';
          tryAgainAnchor.style.marginLeft = '0';
          tryAgainAnchor.href = '#';
          tryAgainAnchor.textContent = translations['App.Notifications.TryAgain'];
          tryAgainAnchor.addEventListener('click', (e) => {
            e.preventDefault();
            onTryAgain();
          });
          contentContainer.appendChild(tryAgainAnchor);

          card.appendChild(contentContainer);
        });

      container.appendChild(this.wrapCard(card));
    } else {
      console.error(`Container with ID '${containerId}' not found.`);
    }
  }

  private createNotification(containerId: string, message: string, notificationType: string): void {
    const container = document.getElementById(containerId);

    if (container) {
      const card = document.createElement('sdx-card');
      card.setAttribute('layout', 'inline-notification');
      card.setAttribute('notification-type', notificationType);
      card.setAttribute('label', message);
      card.setAttribute('label-aria-level', '4');
      container.appendChild(this.wrapCard(card));
    } else {
      console.error(`Container with ID '${containerId}' not found.`);
    }
  }

  private wrapCard(card: HTMLElement): HTMLDivElement {
    const wrapper = document.createElement('div');
    wrapper.style.borderRadius = '12px';
    wrapper.style.background = 'var(--sdx-color-horizon)';
    wrapper.style.padding = '16px';
    wrapper.style.overflow = 'hidden';
    wrapper.appendChild(card);
    return wrapper;
  }
}
